/**
 * 制約の宣言（NE §3）— 新設 29 項目（42〜70）と差し替え 4 項目（2 / 5 / 12 / 18）。パッケージの
 * 公開口は、この表を `SPEC_CONSTRAINT_RULES` の名前で出す。
 *
 * | 区分 | 番号 | 効き方 |
 * |---|---|---|
 * | 差し替え | 2 / 5 / 12 / 18 | 2 は形式検査、残りはリリースゲート |
 * | 新設 | 42〜69 | すべてリリースゲート（設計書 NE §3） |
 *
 * **番号は設計書のとおりで、欠番も詰め直しもしない。** 42〜69 は設計書の表の行と 1 対 1 で、
 * 番号がそのまま設計書への索引になる。詰め直すと、違反の番号から設計書の行を引けなくなる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの制約 33 項目（新設 42〜70 と差し替え 2 / 5 / 12 / 18）の宣言表と、番号からの lookup・違反の重さ
 * @graph-connects none
 */
/**
 * 作り直しの制約の宣言表 33 項目（設計書 NE §3）。番号の昇順。
 *
 * 配列の添字と番号は一致しない（42〜70 の前に差し替えの 4 項目が並ぶ）。番号は設計書の行を
 * 指す索引なので、添字との対応を不変条件にはしない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの制約 33 項目の宣言表。番号・対象・効くタイミング・fix ヒント方針の単一ソース
 * @graph-connects none
 */
export const SPEC_ELEMENT_CONSTRAINT_RULES = [
    {
        constraintNumber: 2,
        id: 'required-attributes',
        description: '説明（description）を持つのは用語・アクター・ドメイン・ステージの 4 型だけで、ほかの型は説明の欄そのものを持たない。承認制で件数が増えない型の説明だけが腐らないので、線を「件数 × 書き手」で引く',
        targetNodeTypes: ['Term', 'Actor', 'Domain', 'Stage'],
        targetEdgeTypes: [],
        enforcement: 'write-time-schema',
        scope: 'input',
        fixHint: '説明は用語・アクター・ドメイン・ステージにだけ書く。ほかの型は名前の作りと欄が意味を持つ',
    },
    {
        constraintNumber: 5,
        id: 'implementation-reference-exists',
        description: '処理の implementedBy が指す qualifiedName が SPG に実在すること。宣言は実装に先行できるため未実在は警告に留める（宛先が操作から処理に替わった）',
        targetNodeTypes: ['Rule'],
        targetEdgeTypes: ['IMPLEMENTED_BY_API', 'IMPLEMENTED_BY_TASK'],
        enforcement: 'release-gate',
        scope: 'external',
        fixHint: '実在する境界名を確かめ、処理の implementedBy を書き直す',
    },
    {
        constraintNumber: 12,
        id: 'term-vocabulary-uniqueness',
        description: '用語と動詞の表記が 1 意味 1 名前で一意であること。同じ表記を 2 つのノードが持つと、本文からどちらを指すかが決まらない（対象を Verb まで広げた）',
        targetNodeTypes: ['Term', 'Verb'],
        targetEdgeTypes: ['USES_TERM'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '同じ表記を持つ 2 件のどちらかを言い換えとして畳むか、表記を書き分ける',
    },
    {
        constraintNumber: 18,
        id: 'db-reference-exists',
        description: '処理の usesTable / usesColumn が指す表と列が db-graph に実在すること（宛先が操作から処理に替わった）',
        targetNodeTypes: ['Rule'],
        targetEdgeTypes: ['USES_TABLE', 'USES_COLUMN'],
        enforcement: 'release-gate',
        scope: 'external',
        fixHint: 'db-graph 照合で実在する表名 / 列名を確かめ、処理の DB 参照を書き直す',
    },
    {
        constraintNumber: 42,
        id: 'business-rule-reference-exists',
        description: '業務の rules が指す業務ルールが実在すること。宙吊りの参照を持つ業務は依存を導出できない',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: ['USES_RULE'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '集合に載せる業務ルールを先に書くか、指した番号の綴りを確かめて業務を書き直す',
    },
    {
        constraintNumber: 43,
        id: 'rule-dependency-acyclic',
        description: '業務ルールの依存に環が無いこと。A の入力が B の出力で、B の入力が A の出力だと評価順が決まらない',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: ['USES_RULE', 'READS_RULE'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '環を作っている業務ルールの入力か出力を見直し、どちらかを別の業務ルールに割る',
    },
    {
        constraintNumber: 44,
        id: 'rule-output-read',
        description: '業務ルールの出力が、別の業務ルールの入力か、業務の出力か、結末の結果のどれかであること。誰にも読まれない出力は書いても効かない',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: ['USES_RULE', 'READS_RULE'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: 'その出力を読む業務ルールか業務の出力を足すか、効かない業務ルールを集合から外す',
    },
    {
        constraintNumber: 45,
        id: 'rule-input-supplied',
        description: '業務ルールの入力が、業務の inputs か、記録された値（用語のプロパティ）か、別の業務ルールの出力のどれかであること。どこからも来ない入力は宙吊り',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: ['USES_RULE', 'READS_RULE'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '業務の inputs に入力を足すか、その値を出力する業務ルールを集合に足す',
    },
    {
        constraintNumber: 46,
        id: 'rule-has-implementation',
        description: '処理が実装（implementedBy）を 1 件以上持つこと。実装 0 件の処理は割り当てが済んでいない（今の制約 20・21 の役目を引き取る）',
        targetNodeTypes: ['Rule'],
        targetEdgeTypes: ['IMPLEMENTED_BY_API', 'IMPLEMENTED_BY_TASK'],
        enforcement: 'release-gate',
        scope: 'aggregate',
        fixHint: 'その処理を実現している API か Task を implementedBy に並べる',
    },
    {
        constraintNumber: 47,
        id: 'reference-section-matches',
        description: '欄が参照先に期待する区画と表現の型が合っていること。入力と母集合はクラス、判定表の欄はプロパティ、並べ替えのキーは順序を持つ型、結末の結果は値、プロパティの属するクラスはクラス、取りうる値は値である',
        targetNodeTypes: ['Term', 'Rule', 'Business'],
        targetEdgeTypes: ['USES_TERM'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: 'その欄が期待する区画の用語を指すよう、番号を書き直す',
    },
    {
        constraintNumber: 48,
        id: 'decision-table-cell-in-value-domain',
        description: '判定表のセルと出力が、その列のプロパティが取りうる値に入っていること。値域に無い値を書いた行は当たらない',
        targetNodeTypes: ['Rule'],
        targetEdgeTypes: [],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '列のプロパティの「取りうる値」から選び直すか、プロパティ側に値を足す',
    },
    {
        constraintNumber: 49,
        id: 'formula-unit-consistency',
        description: '式の加減（add / subtract）が束ねる項の単位が揃っていること。円と率を足す式は読み方が決まらない',
        targetNodeTypes: ['Rule'],
        targetEdgeTypes: ['MEASURED_IN_UNIT'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '段を分けて単位を揃えるか、項の用語の単位を書き直す',
    },
    {
        constraintNumber: 50,
        id: 'mutation-target-transition-valid',
        description: '変更の処理の「変える先」が、対象のクラスのプロパティが取りうる値であり、状態のプロパティなら状態遷移の先に現れること。状態のプロパティの状態遷移の今の値と次の値も、そのプロパティの取りうる値に入っていること。ありえない状態遷移を書けない',
        targetNodeTypes: ['Term', 'Rule'],
        targetEdgeTypes: [],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '対象のクラスのプロパティの値域と状態遷移を確かめ、変える先を選び直す',
    },
    {
        constraintNumber: 51,
        id: 'ending-covers-result-values',
        description: '結末の表が、結果のプロパティが取りうる値を覆っていること。書かれていない値の結末は抜けである',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: [],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '覆えていない結果の値の行を結末の表に足す',
    },
    {
        constraintNumber: 52,
        id: 'flow-branch-covers-endings',
        description: 'フローの業務の節点から出る枝が、その業務の結末の行をすべて指していること。行数と枝の数の差が行き先の抜けになる。結末が 0 行の業務では何も検出しないので、先に 60 が立つ',
        targetNodeTypes: ['Flow'],
        targetEdgeTypes: ['INCLUDES_BUSINESS'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '指されていない結末の行（結果の値）を ending に持つ枝を、フローの transitions に足す',
    },
    {
        constraintNumber: 53,
        id: 'exit-carries-declared-ending',
        description: '「出口へ」の枝が運ぶ結末の行が、そのフローの出口が宣言した結末の行のどれかであること。宣言した結末の行から出る枝は「出口へ」であること。主フローが宣言する行は出口（ステージの出口の結末の行）、支援 3 型は出口に着く結末の行（endings）',
        targetNodeTypes: ['Flow'],
        targetEdgeTypes: ['INCLUDES_BUSINESS'],
        enforcement: 'release-gate',
        scope: 'aggregate',
        fixHint: '「出口へ」の枝の結末の行を出口の宣言に足すか、枝の宛先の型を替える。宣言した結末の行から出る枝は「出口へ」にする',
    },
    {
        constraintNumber: 54,
        id: 'destination-has-target',
        description: '「次へ」「戻る」の枝が to を持ち、「出口へ」の枝を持つフローが出口を持つこと。行き先の無い枝は辿れない',
        targetNodeTypes: ['Flow'],
        targetEdgeTypes: [],
        enforcement: 'release-gate',
        scope: 'aggregate',
        fixHint: '「次へ」「戻る」には次の節点を to に書き、「出口へ」の枝を持つフローには exit を書く',
    },
    {
        constraintNumber: 55,
        id: 'state-term-has-transitions',
        description: '表現の型が「状態」のプロパティが状態遷移を 1 本以上持つこと。必須は表現の型から決まり、人が印を付けるものではない',
        targetNodeTypes: ['Term'],
        targetEdgeTypes: [],
        enforcement: 'release-gate',
        scope: 'aggregate',
        fixHint: 'そのプロパティの状態遷移（今の値・出来事・次の値）を書く',
    },
    {
        constraintNumber: 56,
        id: 'measured-term-has-unit',
        description: '単位が要る表現の型（個数 / 量 / 金額 / 日時）のプロパティが単位を持つこと。単位が無いと数の読み方が決まらない',
        targetNodeTypes: ['Term'],
        targetEdgeTypes: ['MEASURED_IN_UNIT'],
        enforcement: 'release-gate',
        scope: 'aggregate',
        fixHint: 'そのプロパティの unit に単位（Unit マスター）の番号を書く',
    },
    {
        constraintNumber: 57,
        id: 'business-has-consequence',
        description: '業務が帰結を持つこと。帰結は「変更の処理を並びに持つ」「部品から操作される」「Task か GET 以外の API を実装に持つ処理を並びに持つ」のいずれかである（制約 35 の後継）',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: ['USES_RULE', 'OPERATES_BUSINESS'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '変更の処理を並びに足すか、この業務を操作する部品を書く',
    },
    {
        constraintNumber: 58,
        id: 'verb-layer-matches-consequence',
        description: '業務の動詞の層と帰結の種類が合っていること。永続データの層なら変更の処理か GET 以外の実装先、画面の層なら部品からの操作が要る（制約 36 の後継）',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: ['USES_RULE', 'OPERATES_BUSINESS'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '動詞の層に合う帰結を足すか、業務の動詞を実態に合うものへ替える',
    },
    {
        constraintNumber: 59,
        id: 'flow-nodes-reachable',
        description: 'フローの全節点が開始から到達でき、終了か宛先の型を持つ枝へ到達すること。52 は結末の行を覆えているかしか見ないので、枝が 1 本も無いフローを検出できない',
        targetNodeTypes: ['Flow'],
        targetEdgeTypes: [],
        enforcement: 'release-gate',
        scope: 'aggregate',
        fixHint: '開始から届かない節点へ枝を張るか、行き止まりの節点から終了か宛先の型を持つ枝を出す',
    },
    {
        constraintNumber: 60,
        id: 'business-has-ending',
        description: '業務が結末の行を 1 行以上持つこと。51（結果の値を覆う）と 52（枝が行を指す）は結末が 0 行だと何も検出しないので、結末の表そのものの欠落はここで見る',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: [],
        enforcement: 'release-gate',
        scope: 'aggregate',
        fixHint: '業務の結果が取りうる値ごとに、結末の行を endings に足す',
    },
    {
        constraintNumber: 61,
        id: 'stage-exit-exists',
        description: 'ステージ（横断ステージ以外）が出口を持ち、出口が実在する業務の結末の行を指すこと。区切りの 3 条件（全員が通る / 記録で分かる / 対象に見える結果）は人が確かめ、機械は実在だけを見る',
        targetNodeTypes: ['Stage'],
        targetEdgeTypes: ['EXITS_AT'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '区切りになる業務と、その業務の結末の行（結果の値）を exit に書く',
    },
    {
        constraintNumber: 62,
        id: 'stage-has-one-main-flow',
        description: 'ステージが主フローをちょうど 1 本持つこと。主フローは出口の業務を節点に持つフローで、0 本（出口の業務をどのフローも通らない）も 2 本以上（出口の業務を 2 つのフローが通る）も違反になる',
        targetNodeTypes: ['Stage'],
        targetEdgeTypes: ['BELONGS_TO_STAGE', 'EXITS_AT', 'INCLUDES_BUSINESS'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '出口の業務を通るフローが 1 本だけになるよう、主フローを書くか、重なったフローを 1 本に畳む',
    },
    {
        constraintNumber: 63,
        id: 'flow-kind-matches-role',
        description: '宣言した型と、値の流れから導出した役割が一致すること。入れると宣言したのに渡す先の判定表が書いた値を読まない、出すと宣言したのに書いた値を主フローが読む、主と宣言したのに出口がステージの出口でない、どの役割にも当たらない支援フロー、は食い違う',
        targetNodeTypes: ['Flow'],
        targetEdgeTypes: ['FEEDS_BUSINESS', 'OBSERVES_BUSINESS'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: 'フローの型を値の流れに合うものへ替えるか、フローの業務の業務ルールを置き場に合わせて直す',
    },
    {
        constraintNumber: 64,
        id: 'parallel-split-has-join',
        description: '並列の分岐（parallelSplit）が合流（parallelJoin）を持つこと',
        targetNodeTypes: ['Flow'],
        targetEdgeTypes: [],
        enforcement: 'release-gate',
        scope: 'aggregate',
        fixHint: 'parallelSplit から枝で辿って届く位置に parallelJoin を置く',
    },
    {
        constraintNumber: 65,
        id: 'rule-output-unique-in-business',
        description: '同じ業務の rules の中で、同じ出力を持つ業務ルールが 1 つだけであること。「業務の出力 1 つにつき業務ルール 1 つ」の下限で、出力が重なる業務ルールは 1 つに畳める',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: ['USES_RULE'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '同じ出力を決める業務ルールを 1 つに畳み、業務の rules から重なりを外す',
    },
    {
        constraintNumber: 66,
        id: 'flow-size-within-limits',
        description: 'フローの要素が 50 まで、レーン（節点の業務のアクター）が 2〜4 であること。1 図に収まらないフローは止めずに警告で出す',
        targetNodeTypes: ['Flow'],
        targetEdgeTypes: ['INCLUDES_BUSINESS', 'PERFORMED_BY_ACTOR'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '主フローから外れる経路を支援フローとして別の図に割る',
    },
    {
        constraintNumber: 67,
        id: 'business-result-rule-unique',
        description: '結末の結果を決める業務ルール（結果のプロパティを output に持つ最上位の業務ルール）が、業務につきちょうど 1 つであること。0 なら結末が決まらず、2 以上なら結末が競合する',
        targetNodeTypes: ['Business'],
        targetEdgeTypes: ['USES_RULE', 'READS_RULE'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '結果のプロパティを出力に持つ業務ルールを 1 つだけ集合に置く',
    },
    {
        constraintNumber: 68,
        id: 'flow-entry-exit-match-kind',
        description: 'フローの入口と出口が型と合い、相手が実在すること。主の出口がステージの出口と違う、入れる型の出口の業務が同じステージの主フローの節点でない、割り込み型の出口のステージが鎖でこのステージより前にない（FOLLOWS を遡って到達できない）、入口の参照先が無い、は食い違う',
        targetNodeTypes: ['Flow'],
        targetEdgeTypes: ['ENTERS_AT', 'HANDS_OFF_TO_BUSINESS', 'RETURNS_TO_STAGE', 'EXITS_AT'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '入口と出口の参照先を、型が求める相手（ステージの出口・主フローの業務・前のステージ）に書き直す',
    },
    {
        constraintNumber: 69,
        id: 'support-flow-trigger-in-stage',
        description: '入口が「主フローの結末」の支援フローは、その結末の行が同じステージの主フローの業務のものであること。別のステージの結末を引き金にした支援フローは置き場を誤っている',
        targetNodeTypes: ['Flow'],
        targetEdgeTypes: ['ENTERS_AT', 'BELONGS_TO_STAGE'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '引き金の結末を同じステージの主フローの業務から選ぶか、フローを引き金のあるステージへ移す',
    },
    {
        constraintNumber: 70,
        id: 'stage-entry-exists',
        description: 'ステージの入口が実在し、鎖に環が無いこと。入口の参照先（業務, 結末の行）が無い、その業務を出口に持つステージが無い（前のステージが見つからない）、FOLLOWS を辿ると自分に戻る、は違反になる。最初のステージと横断ステージは入口を持たないので対象外',
        targetNodeTypes: ['Stage'],
        targetEdgeTypes: ['STARTS_AT', 'FOLLOWS', 'EXITS_AT'],
        enforcement: 'release-gate',
        scope: 'graph',
        fixHint: '入口には前のステージの出口と同じ業務の結末の行を書く。ステージをまたいで戻るのは割り込み型のフローの出口で表し、入口では表さない',
    },
];
/**
 * 作り直しの制約番号から宣言を引く。表に無い番号には `undefined` を返す。
 *
 * @param constraintNumber 設計書 NE §3 の制約番号
 * @returns 宣言。表に無ければ undefined
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの制約番号から宣言を解決する lookup
 * @graph-connects none
 */
export function findElementConstraintRule(constraintNumber) {
    return SPEC_ELEMENT_CONSTRAINT_RULES.find((rule) => rule.constraintNumber === constraintNumber);
}
/**
 * リリースゲートで判定する作り直しの制約だけを返す。
 *
 * @returns 効き方が `release-gate` の宣言
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 監査と完成判定が評価する作り直しの制約の抽出（形式検査で止まるものを除く）
 * @graph-connects none
 */
export function elementReleaseGateConstraints() {
    return SPEC_ELEMENT_CONSTRAINT_RULES.filter((rule) => rule.enforcement === 'release-gate');
}
/**
 * 違反を警告（`warning`）として出す規則 ID の閉集合。ここに無い規則の違反は `error` である。
 *
 * 警告に落とすのは、仕様の不備ではないものだけである。5 と 18 は宣言が実装や DB に先行できる
 * ので、未実在は紐づけの未登録にすぎない。66 は図の大きさの目安で、超えても仕様は壊れない。
 *
 * 重さを宣言表の列にしないのは、列にするとこの集合と同じ事実を 2 か所に書くことになり、
 * 規則を足したときに食い違えるからである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの制約のうち、違反を止めずに警告として出す規則の allowlist
 * @graph-connects none
 */
export const SPEC_ELEMENT_WARNING_RULE_IDS = [
    'implementation-reference-exists',
    'db-reference-exists',
    'flow-size-within-limits',
];
/**
 * 規則 ID から作り直しの制約違反の重さを決める。
 *
 * @param ruleId 制約 rule の ID（`SPEC_ELEMENT_CONSTRAINT_RULES` の `id`）
 * @returns 警告の閉集合に入っていれば `warning`、それ以外は `error`
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 規則 ID から作り直しの制約違反の重さ（仕様の不備 / 止めない警告）を導出する
 * @graph-connects none
 */
export function elementConstraintSeverity(ruleId) {
    return SPEC_ELEMENT_WARNING_RULE_IDS.includes(ruleId) ? 'warning' : 'error';
}
/**
 * 作り直しの制約が対象にするノード型の閉集合。宣言表の `targetNodeTypes` がここから外れて
 * いないことをテストが突き合わせる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 作り直しの制約が宛先にできるノード型の allowlist
 * @graph-connects none
 */
export const ELEMENT_CONSTRAINT_TARGET_NODE_TYPES = [
    'Term',
    'Verb',
    'Actor',
    'Domain',
    'Stage',
    'Flow',
    'Rule',
    'Business',
];
//# sourceMappingURL=constraint-rules.js.map